import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { Calendar, RefreshCw, DollarSign, AlertCircle, Clock, Home as HomeIcon } from "lucide-react";

export default function CancellationRefundPolicy() {
  const sections = [
    {
      id: "rescheduling",
      title: "Rescheduling",
      icon: RefreshCw,
      items: [
        "If you are unable to make it on your booked date, we highly recommend rescheduling.",
        "Rescheduling is allowed up to a week before the stay date, subject to availability."
      ]
    },
    {
      id: "refund-policy",
      title: "Refund Policy",
      icon: DollarSign,
      items: [
        "Full refund if canceled at least 5 days before check-in.",
        "No refund if canceled within 5 days of check-in.",
        "No refunds will be provided for cancellations due to unforeseen weather conditions or government restrictions.",
        "Refunds will be processed within 2 working days and credited to the same bank account used for booking."
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Header />
      <PageBanner
        title="Cancellation & Refund Policy"
        description="Flexible cancellation options for your peace of mind"
        backgroundImage="/assets/banner-bg.jpg"
      />
      
      <main className="flex-grow">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Introduction */}
          <div className="bg-gradient-to-r from-[#E67E22]/10 to-[#F39C12]/10 border-l-4 border-[#E67E22] rounded-lg p-6 mb-12">
            <div className="flex items-start">
              <AlertCircle className="text-[#E67E22] mr-4 flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="font-serif text-xl font-bold text-[#2C3E50] mb-2">
                  Understanding Cancellations
                </h2>
                <p className="text-[#555] leading-relaxed">
                  We understand that plans may change. Please review our cancellation and refund policy before booking.
                </p>
              </div>
            </div>
          </div>

          {/* Policy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={section.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 p-8 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#E67E22] to-[#F39C12] flex items-center justify-center mr-4">
                    <section.icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#2C3E50]">
                    {index + 1}. {section.title}
                  </h3>
                </div>
                <ul className="space-y-3 ml-16">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start group">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#E67E22] mr-3 mt-2 flex-shrink-0 group-hover:scale-125 transition-transform"></span>
                      <span className="text-[#555] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Quick Reference Guide */}
          <div className="mt-12 bg-white rounded-xl shadow-md p-8">
            <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-6 text-center">
              Quick Reference Guide
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <Calendar className="text-green-600 mr-3" size={24} />
                  <h4 className="font-bold text-green-800">5+ Days Before Check-in</h4>
                </div>
                <p className="text-green-700 text-sm">
                  ✓ Full refund available<br />
                  ✓ Free rescheduling (subject to availability)
                </p>
              </div>
              
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <Clock className="text-red-600 mr-3" size={24} />
                  <h4 className="font-bold text-red-800">Within 5 Days of Check-in</h4>
                </div>
                <p className="text-red-700 text-sm">
                  ✗ No refund available<br />
                  ✓ Rescheduling may be possible (contact us)
                </p>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-8 bg-gradient-to-r from-[#2C3E50] to-[#34495E] text-white rounded-xl p-8">
            <div className="flex items-start">
              <AlertCircle className="text-[#F39C12] mr-4 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-serif text-xl font-bold mb-3">Important Notice</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  To request a cancellation or reschedule your booking, please contact us as soon as possible:
                </p>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center">
                    <span className="text-[#F39C12] mr-2">📧</span>
                    <a href="mailto:support@wyldwoods.in" className="text-[#F39C12] hover:underline">
                      support@wyldwoods.in
                    </a>
                  </p>
                  <p className="flex items-center">
                    <span className="text-[#F39C12] mr-2">📞</span>
                    <a href="tel:+919445771282" className="hover:text-white transition-colors">
                      +91 94457 71282
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center text-[#E67E22] hover:text-[#F39C12] font-semibold transition-colors"
            >
              <HomeIcon size={20} className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
